import React, { Component } from "react";
import {
  FaPauseCircle,
  FaPlayCircle,
  FaStepBackward,
  FaStepForward,
  FaTimes,
} from "react-icons/fa";
import { observer } from "mobx-react";
import WaveSurfer from "wavesurfer.js";
import voiceStore from "../stores/voiceStore";
import { LuHeart } from "react-icons/lu";
import generalStore from "../stores/generalStore";

const BottomPlayer = observer(
  class BottomPlayer extends Component {
    constructor(props) {
      super(props);
      voiceStore.waveSurfer = null;
      this.state = {
        isVisible: true,
        duration: 0,
        currentTime: 0,
        skipForwardDisable: false,
        skipBackwardDisable: false,
      };
    }

    componentDidMount() {
      this.fetchPeaksAndCreateWaveSurfer();
    }

    componentWillUnmount() {
      if (voiceStore.waveSurfer) {
        voiceStore.waveSurfer.destroy();
      }
    }

    fetchPeaksAndCreateWaveSurfer = async () => {
      try {
        const response = await fetch(
          voiceStore.currentVoice?.voice_modulation_path
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const peaks = await response.json();
        const numericPeaks = peaks.map(parseFloat);
        if (this.validatePeaks(numericPeaks)) {
          this.createWaveSurfer(numericPeaks);
        } else {
          throw new Error("Invalid peaks format");
        }
      } catch (error) {
        console.error("Error fetching peaks:", error);
        this.createWaveSurfer();
      }
    };

    validatePeaks = (peaks) => {
      return (
        Array.isArray(peaks) &&
        peaks.every((value) => typeof value === "number")
      );
    };

    createWaveSurfer = (peaks) => {
      const container = document.getElementById(`waveform`);
      if (!container) {
        console.error("Waveform container not found");
        return;
      }

      voiceStore.waveSurfer = WaveSurfer.create({
        container: container,
        waveColor: "gray",
        progressColor: "black",
        height: 23,
        barGap: 1,
        barHeight: 1,
        barWidth: 2,
        autoplay: false,
        loop: false,
      });

      if (peaks) {
        voiceStore.waveSurfer.load(voiceStore.currentVoice?.voice_path, peaks);
      } else {
        voiceStore.waveSurfer.load(voiceStore.currentVoice?.voice_path);
      }

      voiceStore.waveSurfer.on("ready", () => {
        this.setState({ duration: voiceStore.waveSurfer.getDuration() });
      });
      voiceStore.waveSurfer.on("audioprocess", () => {
        this.setState({ currentTime: voiceStore.waveSurfer.getCurrentTime() });
      });
      voiceStore.waveSurfer.on("error", (error) => {
        console.error("Error loading audio:", error);
      });
      voiceStore.waveSurfer.on("finish", () => {
        voiceStore.togglePlayPause(false);
        this.setState({ isPlaying: false });
        voiceStore.waveSurfer.seekTo(0);
      });
    };

    componentDidUpdate(prevProps) {
      // 1. Ensure waveSurfer exists before calling methods on it
      if (!voiceStore.waveSurfer) return;

      // 2. Check if the playing state has actually changed
      // This prevents redundant .play() calls on every re-render
      const isCurrentlyPlaying = voiceStore.isPlaying;
      const wasPlaying = voiceStore.waveSurfer.isPlaying();

      if (isCurrentlyPlaying && !wasPlaying) {
        voiceStore.waveSurfer.play();
      } else if (!isCurrentlyPlaying && wasPlaying) {
        voiceStore.waveSurfer.pause();
      }
    }

    togglePlayPause = () => {
      voiceStore.togglePlayPause();
    };

    handleClose = () => {
      this.setState({ isVisible: false });
      setTimeout(() => {
        voiceStore.setCurrentVoice(null);
      }, 500);
    };

    formatTime = (time) => {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };

    toggleSkipBackward = () => {
      if (voiceStore.currentVoice) {
        const currentTalent = voiceStore.currentVoice;
        const currentVoiceIndex = voiceStore.searchResults
          .find((talent) => talent.talent_id === currentTalent.talent_id)
          .voice_data.findIndex(
            (voice) => voice.voice_path === voiceStore.currentVoice.voice_path
          );

        if (currentVoiceIndex > 0) {
          const previousVoice = voiceStore.searchResults.find(
            (talent) => talent.talent_id === currentTalent.talent_id
          ).voice_data[currentVoiceIndex - 1];

          this.setState({ skipBackwardDisable: true });

          voiceStore.setCurrentVoice(null);

          setTimeout(() => {
            voiceStore.setCurrentVoice({
              talent_id: currentTalent.talent_id,
              talent_name: currentTalent.talent_name,
              voice_path: previousVoice.voice_path,
              voice_modulation_path: previousVoice.voice_modulation_path,
              sample_id: previousVoice.sample_id,
              IsFavorite: previousVoice.isFavorite,
              profile_image_path: previousVoice.profile_image_path,
            });

            voiceStore.togglePlayPause(true);
          }, 500);
        } else {
          this.setState({ skipBackwardDisable: true });
        }
      }
    };

    toggleSkipForward = () => {
      if (voiceStore.currentVoice) {
        const currentTalent = voiceStore.currentVoice;
        const currentVoiceIndex = voiceStore.searchResults
          .find((talent) => talent.talent_id === currentTalent.talent_id)
          .voice_data.findIndex(
            (voice) => voice.voice_path === voiceStore.currentVoice.voice_path
          );

        if (
          currentVoiceIndex <
          voiceStore.searchResults.find(
            (talent) => talent.talent_id === currentTalent.talent_id
          ).voice_data.length -
            1
        ) {
          this.setState({ skipForwardDisable: false });

          const nextVoice = voiceStore.searchResults.find(
            (talent) => talent.talent_id === currentTalent.talent_id
          ).voice_data[currentVoiceIndex + 1];

          voiceStore.setCurrentVoice(null);

          setTimeout(() => {
            voiceStore.setCurrentVoice({
              talent_id: currentTalent.talent_id,
              talent_name: currentTalent.talent_name,
              voice_path: nextVoice.voice_path,
              voice_modulation_path: nextVoice.voice_modulation_path,
              sample_id: nextVoice.sample_id,
              IsFavorite: nextVoice.isFavorite,
              profile_image_path: nextVoice.profile_image_path,
            });

            voiceStore.togglePlayPause(true);
          }, 500);
        } else {
          this.setState({ skipForwardDisable: true });
        }
      }
    };

    handleFavoriteMusic = () => {
      voiceStore.setFavoriteMusic();
    };

    render() {
      const { duration, currentTime, isVisible } = this.state;
      return (
        <div
          className={`z-[51] fixed flex w-full bottom-0 h-16 border border-[#E2E8F0] rounded-t-xl bg-white shadow-2xl ${
            isVisible ? "animate-slideUp" : "animate-slideDown"
          }`}
        >
          <div className="flex flex-1 w-full relative flex-row items-center h-full px-2 lg:px-10">
            <div className="flex flex-row gap-2 items-center justify-start">
              {voiceStore.currentVoice?.profile_image_path ? (
                <img
                  src={
                    process.env.REACT_APP_BASE_URL +
                    "/" +
                    voiceStore.currentVoice?.profile_image_path
                  }
                  className="w-10 h-10 rounded-full"
                  alt="Profile"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex justify-center items-center text-sm text-black/50">
                  {generalStore.getInitials(
                    voiceStore.currentVoice?.talent_name
                  )}
                </div>
              )}
              <div className="flex flex-col justify-start items-start">
                <span className="font-medium text-xs lg:text-sm text-[#130D0D] select-none overflow-hidden whitespace-nowrap text-ellipsis max-w-[100px] lg:max-w-[250px]">
                  {voiceStore.currentVoice?.talent_name}
                </span>
                <button
                  onClick={() => {
                    voiceStore
                      .getTalentById(voiceStore.currentVoice?.talent_id)
                      .then(() => {
                        voiceStore.setTalentDetailsModalVisible(true);
                      });
                  }}
                  className="font-normal text-[8px] lg:text-xs text-[#B7B7B7] select-none"
                >
                  View Profile
                </button>
              </div>
            </div>

            <div className="flex flex-1 flex-row  gap-x-1 lg:gap-x-10 justify-center items-center">
              <div className="flex flex-row gap-x-3">
                <button disabled={this.state.skipBackwardDisable}>
                  <FaStepBackward
                    className={`${
                      this.state.skipBackwardDisable && "text-black/30"
                    }`}
                    onClick={this.toggleSkipBackward}
                    size={20}
                  />
                </button>

                <button onClick={this.togglePlayPause}>
                  {voiceStore.isPlaying ? (
                    <FaPauseCircle size={22} />
                  ) : (
                    <FaPlayCircle size={22} />
                  )}
                </button>

                <button disabled={this.state.skipForwardDisable}>
                  <FaStepForward
                    className={`${
                      this.state.skipForwardDisable && "text-black/30"
                    }`}
                    onClick={this.toggleSkipForward}
                    size={20}
                  />
                </button>
              </div>
              <div id="waveform" className="w-0 md:w-[50%]"></div>
              <div className="font-medium text-xs text-[#130D0D] select-none">
                {this.formatTime(currentTime)} / {this.formatTime(duration)}
              </div>
              <LuHeart
                onClick={this.handleFavoriteMusic}
                className={`hover:cursor-pointer hidden lg:flex ${
                  voiceStore.currentVoice.IsFavorite === "1" && "fill-[#F89A1F]"
                }`}
                size={18}
              />
            </div>
            <FaTimes
              onClick={this.handleClose}
              style={{ right: 16, top: 15 }}
              className="flex w-4 h-4 lg:w-6 lg:h-6 lg:absolute lg:right-4 lg:top-3 hover:cursor-pointer"
            />
          </div>
        </div>
      );
    }
  }
);

export default BottomPlayer;
